import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import type { CustomerPortalTokenRow } from '../domain/customerTypes';
import { getCustomerAccountById } from './customerAccounts';
import { invalidateWebCustomerSnapshotAfterWrite, refreshWebCustomerSnapshotAfterWrite } from './customerRepositoryShared';
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;
// La Edge Function real es `fiado-payment-link` (supabase/functions/). Un nombre
// viejo (`customer-payment-link`) devolvía 404 y el usuario veía un error genérico.
const CUSTOMER_PAYMENT_FUNCTION_URL = SUPABASE_URL ? `${SUPABASE_URL}/functions/v1/fiado-payment-link` : null;

const mapPortalTokenRow = (row: any): CustomerPortalTokenRow => ({
  customerId: row.customer_id,
  token: row.token,
  paymentLinkUrl: row.payment_link_url,
  qrImageUrl: row.qr_image_url,
  expectedAmount: row.expected_amount,
  expiresAt: row.expires_at,
  lastPaymentStatus: row.last_payment_status,
  lastPaymentAmount: row.last_payment_amount,
  lastPaymentAt: row.last_payment_at,
  lastPaymentReference: row.last_payment_reference,
});

const isTokenValid = (row?: CustomerPortalTokenRow | null) => {
  if (!row?.expiresAt) return false;
  return new Date(row.expiresAt).getTime() > Date.now() + 5 * 60 * 1000;
};

/** Evita mostrar JSON crudo en Alert cuando la Edge Function devuelve { error, requestId }. */
const parseCustomerFunctionErrorBody = (body: string): string => {
  const trimmed = body.trim();
  if (!trimmed) return 'No se pudo obtener el link de pago.';
  try {
    const j = JSON.parse(trimmed) as { error?: string; message?: string };
    if (typeof j.error === 'string' && j.error.length > 0) {
      if (j.error === 'Cliente no encontrado') {
        return 'El cliente no está en el servidor. Revisá la conexión o sincronizá desde el ícono de nube.';
      }
      return j.error;
    }
    if (typeof j.message === 'string' && j.message.length > 0) return j.message;
  } catch {
    if (trimmed.length < 240) return trimmed;
  }
  return 'No se pudo obtener el link de pago.';
};

/** Datos locales enviados a la Edge Function para upsert en Supabase si el cliente aún no existe allí. */
async function loadCustomerSnapshotForPortal(customerId: string): Promise<{
  id: string;
  name: string;
  phone: string | null;
  cuit: string | null;
  credit_limit: number;
  balance: number;
  blocked: boolean;
} | null> {
  const customer = await getCustomerAccountById(customerId);
        if (!customer) return null;
        return {
          id: customer.id,
          name: customer.name,
          phone: customer.phone || null,
          cuit: customer.cuit ?? null,
          credit_limit: customer.creditLimit,
          balance: customer.balance,
          blocked: customer.blocked,
        };
}

export const getOrCreatePortalToken = async (
  customerId: string,
  opts?: { forceRefresh?: boolean }
): Promise<CustomerPortalTokenRow> => {
  const forceRefresh = opts?.forceRefresh ?? false;

  if (!forceRefresh) {
    const customer = await getCustomerAccountById(customerId);
            const mapped = customer?.portalToken ?? null;
            if (isTokenValid(mapped)) {
              return mapped!;
            }
  }

  if (!CUSTOMER_PAYMENT_FUNCTION_URL || !SUPABASE_ANON_KEY) {
    throw new Error('No se configuró la función de links de pago. Revisá las variables EXPO_PUBLIC_SUPABASE_*');
  }

  let accessToken: string | undefined = useAuthStore.getState().session?.access_token;
  if (!accessToken) {
    const { data: sessionData } = await supabase.auth.getSession();
    accessToken = sessionData.session?.access_token;
  }
  if (!accessToken) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    accessToken = refreshed.session?.access_token;
  }
  if (!accessToken) {
    throw new Error('Iniciá sesión para generar el link de pago.');
  }

  const customerSnapshot = await loadCustomerSnapshotForPortal(customerId);
  if (!customerSnapshot) {
    throw new Error('Cliente no encontrado en el dispositivo.');
  }

  const response = await fetch(CUSTOMER_PAYMENT_FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ customerId, customerSnapshot }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseCustomerFunctionErrorBody(errorText));
  }

  const payload = await response.json();
  const { token, paymentLinkUrl, qrImageUrl, expiresAt, expectedAmount } = payload;

  // ADR-017 Fase 4: cola eliminada — sin INSERT local en customer_portal_tokens

  invalidateWebCustomerSnapshotAfterWrite();
  await refreshWebCustomerSnapshotAfterWrite();
  return {
    customerId,
    token,
    paymentLinkUrl,
    qrImageUrl,
    expectedAmount,
    expiresAt,
  };
};
